import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { HttpStatusCode } from "axios";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import type { SentAttachmentOption } from "messaging/_views/components/editor/message-editor";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { describeWorkAgreementConversation } from "work/work-agreements/correspondence/work-agreement-conversation-titles";
import Guid from "_shared/guids/guid";
import { DocumentCopyResultJSON, useCopyAttachmentToDocumentsMutation } from "documents/_api/document-api";
import DocumentTopic from "documents/topics/document-topic";
import { workDocumentsTopic } from "work/work-agreements/documents/_api/work-agreement-documents-api";
import { detailOf, statusOf } from "work/work-agreements/documents/_views/work-agreement-documents-panel";

/** The room everyone on the work reads. Its files are already readable by everyone on the work. */
const EVERYONE_FORUM_NAME = "all stakeholders";

/** What the sender is told when a ticked "Also add to the work's documents" could not run at all. */
export const NOT_ADDED_WHEN_SENT_MESSAGE = "The file was sent, but it couldn't be added to the work's documents.";

type CopyAttachment = ReturnType<typeof useCopyAttachmentToDocumentsMutation>[0];

/**
 * Copy to documents, for a work agreement's chat (#6053): asks the reader to confirm, copies the
 * attachment into the work's documents, and tells them what happened -- copied, already there, or
 * refused and why.
 *
 * Undefined until the agreement is known, so the chat offers no button it cannot act on.
 */
export default function useCopyAttachmentToWorkDocuments(
  conversationId: string | undefined,
): ((attachment: MessageFileInfo, forum: Forum) => Promise<void>) | undefined {
  const confirm = useConfirmDialog();
  const [copy] = useCopyAttachmentToDocumentsMutation();

  const conversation = Guid.fromJSON(conversationId);
  if (!conversation) return undefined;
  const topic = workDocumentsTopic(conversation);

  return async function copyAttachment(attachment: MessageFileInfo, forum: Forum) {
    if (!forum.id?.value) {
      reportNotCopied(attachment);
      return;
    }

    const answer = await confirm({
      title: "Copy to documents?",
      message: copyConfirmationMessage(attachment.name, forum),
      okButtonText: "Copy",
      cancelButtonText: "Cancel",
    });
    if (answer !== ConfirmResponse.Ok) return;

    await copyToWorkDocuments(copy, topic, attachment, forum);
  };
}

/**
 * "Also add to the work's documents", beside a file attached in a work agreement's chat (#6098). Once
 * the message is sent, each of its files is copied as Copy to documents copies it, and the sender is
 * told what became of each. The file stays in the message whatever happens to the copy.
 *
 * Undefined until the agreement is known, so the editor offers no choice it cannot act on.
 */
export function useAddSentAttachmentsToWorkDocuments(conversationId: string | undefined): SentAttachmentOption | undefined {
  const [copy] = useCopyAttachmentToDocumentsMutation();

  const conversation = Guid.fromJSON(conversationId);
  if (!conversation) return undefined;
  const topic = workDocumentsTopic(conversation);

  return {
    label: "Also add to the work's documents",
    describe: addWhenSentMessage,
    onSent: async (attachments, forum) => {
      // Until each file's outcome is shown, the sender is told the copy is under way (#6597).
      enqueueSnackbar(addingMessage(attachments), { variant: "info" });
      // One at a time, so a file sent twice in a row is answered "already there" rather than racing.
      for (const attachment of attachments) await copyToWorkDocuments(copy, topic, attachment, forum);
    },
    notDone: NOT_ADDED_WHEN_SENT_MESSAGE,
  };
}

/** Copies one attachment into the work's documents and tells the reader what became of it. */
async function copyToWorkDocuments(
  copy: CopyAttachment,
  topic: DocumentTopic,
  attachment: MessageFileInfo,
  forum: Forum,
): Promise<void> {
  const forumId = forum.id?.value;
  if (!forumId) {
    reportNotCopied(attachment);
    return;
  }

  // A copy of the file, under the reader's organization, carrying the work's topic.
  const outcome = await copy({ forumId, fileId: attachment.id.value, topics: [topic] })
    .unwrap()
    .then((result) => ({ result, error: undefined as unknown }))
    .catch((error: unknown) => ({ result: undefined, error }));

  if (!outcome.result) {
    enqueueSnackbar(copyFailureMessage(outcome.error), { variant: "error" });
    return;
  }
  const { message, variant } = copyOutcomeMessage(attachment.name, outcome.result);
  enqueueSnackbar(message, { variant });
}

/** What the sender is told while the sent files are copied. */
function addingMessage(attachments: MessageFileInfo[]): string {
  if (attachments.length === 1) return `Adding “${attachments[0].name}” to the work's documents…`;
  return `Adding ${attachments.length} files to the work's documents…`;
}

/** Never in silence (#6597): the reader asked for a copy, and is told it was not made. */
function reportNotCopied(attachment: MessageFileInfo) {
  enqueueSnackbar(`“${attachment.name}” could not be copied to the work's documents.`, { variant: "warning" });
}

/** A snackbar's text, and how it is shown. */
type CopyOutcome = { message: string; variant: "success" | "info" | "warning" | "error" };

/** What the reader is told about one file the server answered for. */
function copyOutcomeMessage(fileName: string, result: DocumentCopyResultJSON): CopyOutcome {
  switch (result.status) {
    case "added":
      return { message: `“${fileName}” was copied to the work's documents.`, variant: "success" };
    case "already_copied":
      return { message: `“${fileName}” is already in the work's documents.`, variant: "info" };
    case "duplicate":
      // Same content as a document already on the work, which the server names.
      return {
        message: result.issue ?? `“${fileName}” is already in the work's documents.`,
        variant: "warning",
      };
    default:
      return { message: result.issue ?? `“${fileName}” could not be copied.`, variant: "error" };
  }
}

/**
 * What the reader agrees to. A file from a team room was read by one side only; a copy is readable by
 * everyone on the work, so the dialog says so in as many words.
 */
export function copyConfirmationMessage(fileName: string, forum: Forum): string {
  const everyone = `Everyone on the work, on both sides, will be able to open “${fileName}” from Documents.`;
  if (forum.name?.toString().trim().toLocaleLowerCase() === EVERYONE_FORUM_NAME) return everyone;

  const room = describeWorkAgreementConversation(forum)?.title ?? forum.name?.toString() ?? "this conversation";
  return `${everyone} Until now, only the readers of ${room} could open it.`;
}

/**
 * What ticking "Also add to the work's documents" means, said under the checkbox beside the file
 * (#6098). A team room's file would otherwise reach only that room, so the choice says who else will
 * read it. Short, and without the file's name, which is shown beside it: the composer is narrow.
 */
export function addWhenSentMessage(forum: Forum): string {
  const everyone = "Everyone on the work, on both sides, will be able to open it from Documents";
  if (forum.name?.toString().trim().toLocaleLowerCase() === EVERYONE_FORUM_NAME) return `${everyone}.`;

  const room = describeWorkAgreementConversation(forum)?.title ?? forum.name?.toString() ?? "this conversation";
  return `${everyone}, not only the readers of ${room}.`;
}

export function copyFailureMessage(error: unknown): string {
  switch (statusOf(error)) {
    case HttpStatusCode.Forbidden:
      // A refusal that says why - the malware scan found a threat in the file, or could not scan
      // it (#414) - is shown as it is. Only a bare refusal is about access.
      return detailOf(error) ?? "You cannot copy this attachment. Only members of the work who can read it may copy it.";
    case HttpStatusCode.NotFound:
      return "The attachment no longer exists.";
    case HttpStatusCode.BadRequest:
    case HttpStatusCode.Conflict:
      return detailOf(error) ?? "The attachment cannot be copied to the work now.";
    default:
      return "The attachment could not be copied. Try again.";
  }
}
